/**
 * Comments — the per-GAME comment thread on a game's landing page. Public read
 * (anyone sees the conversation); only a signed-in, non-anonymous user may post.
 * A comment's AUTHOR (or a superadmin, for moderation) may delete it. Writes go
 * through the postComment/deleteComment callables (the ONE write path — the
 * rules deny direct client writes); the live thread + count come from the
 * `comments` subscription.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Comment } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { Mascot } from './Mascot.js';
import { UserAvatar } from './UserAvatar.js';

/** Keep in sync with the callable's COMMENT_MAX_LEN. */
const COMMENT_MAX_LEN = 2000;

/** Coarse "how long ago" label (client clock — this is UI, not the sim). */
function timeAgo(ts: number): string {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function Comments({ creationId }: { creationId: string }) {
  const { user } = useAuth();
  const services = useServices();
  const comments = useSubscription<Array<Comment & { id: string }>>(
    (cb) => services.subscribe.comments(creationId, cb),
    [],
    [creationId],
  );
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canComment = Boolean(user && !user.isAnonymous);
  const isAdmin = user?.role === 'superadmin';

  async function post(): Promise<void> {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    try {
      await services.callable.postComment({ creationId, body: text });
      setBody('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post your comment.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(commentId: string): Promise<void> {
    if (!window.confirm('Delete this comment?')) return;
    setError('');
    try {
      await services.callable.deleteComment({ creationId, commentId });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the comment.');
    }
  }

  return (
    <div className="panel" data-testid="comments">
      <h2 className="panel__title">
        {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
      </h2>

      {error && <div className="error-text" data-testid="comment-error">{error}</div>}

      {canComment ? (
        <div className="comment-form">
          <textarea
            className="textarea"
            style={{ marginBottom: 8 }}
            placeholder="Add a comment…"
            value={body}
            maxLength={COMMENT_MAX_LEN}
            rows={3}
            onChange={(e) => setBody(e.target.value)}
            data-testid="comment-input"
          />
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => void post()}
              disabled={busy || body.trim().length === 0}
              data-testid="comment-submit"
            >
              {busy ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </div>
      ) : (
        <p className="muted" data-testid="comment-signin">
          Sign in to join the conversation.
        </p>
      )}

      {comments.length === 0 ? (
        <>
          <Mascot context="comments-empty" size={260} quip={false} />
          <p className="muted" data-testid="comment-empty">
            No comments yet — be the first.
          </p>
        </>
      ) : (
        <ul className="comment-list">
          {comments.map((c) => {
            const mine = user?.uid === c.uid;
            return (
              <li key={c.id} className="comment" data-testid="comment-row">
                <div className="comment__meta">
                  <UserAvatar uid={c.uid} size={20} />
                  {c.handle ? (
                    <Link className="comment__author" to={`/u/${c.handle}`}>
                      @{c.handle}
                    </Link>
                  ) : (
                    <span className="comment__author">@{c.uid.slice(0, 8)}</span>
                  )}
                  <span className="comment__time muted">{timeAgo(c.createdAt)}</span>
                  {(mine || isAdmin) && (
                    <button
                      className="btn btn--ghost btn--sm comment__delete"
                      onClick={() => void remove(c.id)}
                      data-testid={`comment-delete-${c.id}`}
                      title="Delete comment"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="comment__body">{c.body}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
